// ============================================================================
// HAI Reporting Tool - Sync Status Indicator
// Visual indicator for sync status and pending items
// ============================================================================

import { useSyncStatus } from '@/hooks/useAutoSave';
import { syncService } from '@/services/sync.service';
import { useState } from 'react';

export function SyncStatusIndicator() {
  const status = useSyncStatus();
  const [showDetails, setShowDetails] = useState(false);

  const handleSyncNow = async () => {
    await syncService.syncNow();
  };

  if (!status.pendingCount && !status.isSyncing) {
    // All synced - minimal display
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="w-2 h-2 rounded-full bg-success-500"></span>
        <span>Synced</span>
        {status.lastSyncAt && (
          <span className="text-gray-400">
            {formatTimeAgo(status.lastSyncAt)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
      >
        {status.isSyncing ? (
          <>
            <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></div>
            <span className="font-medium text-primary-700">Syncing...</span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 rounded-full bg-warning-500"></div>
            <span className="font-medium text-warning-700">
              {status.pendingCount} pending
            </span>
          </>
        )}
      </button>

      {/* Details Dropdown */}
      {showDetails && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
          <h4 className="font-semibold text-gray-900 mb-3">Sync Status</h4>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-medium">
                {status.isSyncing ? 'Syncing' : 'Idle'}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Pending items:</span>
              <span className="font-medium">{status.pendingCount}</span>
            </div>

            {status.lastSyncAt && (
              <div className="flex justify-between">
                <span className="text-gray-600">Last sync:</span>
                <span className="font-medium">
                  {formatTimeAgo(status.lastSyncAt)}
                </span>
              </div>
            )}

            {status.error && (
              <div className="text-danger-600 text-xs mt-2">
                Error: {status.error}
              </div>
            )}
          </div>

          <button
            onClick={handleSyncNow}
            disabled={status.isSyncing || status.pendingCount === 0}
            className="btn btn-primary w-full mt-4 text-sm"
          >
            {status.isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>

          <button
            onClick={() => setShowDetails(false)}
            className="text-xs text-gray-500 hover:text-gray-700 mt-2 w-full"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Format time ago (e.g., "2 minutes ago")
 */
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
